export const en = {
  nav: {
    services: "Services",
    solutions: "Solutions",
    industries: "Industries",
    work: "Work",
    about: "About",
    insights: "Insights",
    contact: "Contact",
    talkToUs: "Book a fit call",
    seeWhatWeBuild: "Explore production AI systems",
    servicesOverview: "Production AI services",
    industriesOverview: "Industry fit",
    primaryAria: "Primary navigation",
    mobileAria: "Mobile navigation",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },
  trustTicker: {
    builtFor: "Built for",
    items: ["Retail & Ecommerce", "CPG", "Professional Services", "Franchise", "DTC Brands"],
  },
  offerCard: {
    learnMore: "Open the service brief",
  },
  cta: {
    talkToUs: "Book a production AI fit call",
  },
  home: {
    heroLabel: "Production AI · Data foundations · Decision systems",
    heroTitle: "Production AI systems for workflows that need measurable operating impact.",
    heroSubtitle:
      "Vert AI helps operators turn workflow bottlenecks, messy data, and recurring decisions into measurable systems with review paths, monitoring, and clear ownership.",
    executionGapLabel: "The execution gap",
    executionGapTitle: "Most AI fails in the last mile. The model is rarely the problem.",
    executionGapQuote:
      "Pilots that demoed well and never shipped. Dashboards nobody acts on. Six-figure budgets that produced slideware instead of operating systems.",
    executionGapP1:
      "The distance between AI ambition and operating value is an engineering, data architecture, and decision-logic problem. It is the work required to run a system inside a real business, against real data, real constraints, and real users.",
    executionGapP2:
      "We close that gap by scoping the workflow, building the data path, defining the review boundary, and shipping toward a measurable operating baseline.",
    whyUsLabel: "Why Vert AI",
    whyUsTitle: "One firm.\nScope through production.",
    whyUsSubtitle:
      "No handoff between the people who think and the people who build. The team that frames the opportunity is the team that deploys the system.",
    diff1Title: "One team, scope to deploy",
    diff1Desc:
      "Strategy and engineering are not separated. The team that defines the solution designs the architecture, writes the code, and puts the system into production.",
    diff2Title: "Technical depth, commercial judgment",
    diff2Desc:
      "Data science, ML engineering, and AI systems architecture grounded in unit economics and measurable outcomes. We build systems that move the business.",
    diff3Title: "Production systems, not prototypes",
    diff3Desc:
      "Every engagement ships a system that operates inside your business. Monitored, documented, and built to run unattended.",
    diff4Title: "Defined output commitments",
    diff4Desc:
      "Every engagement has written deliverables, acceptance criteria, production risks, and ownership expectations before implementation expands.",
    engagementsLabel: "Engagements",
    engagementsTitle: "Services",
    engagementsSubtitle:
      "Scoped to the workflow, baseline, data reality, and expected operating value. Defined deliverables and acceptance criteria included.",
    offer1Title: "Agentic AI",
    offer1Desc:
      "Automate the high-value workflows that consume your operating capacity. Bounded agents with routing, controls, fallback paths, and human-in-the-loop oversight, deployed end to end.",
    offer2Title: "Generative AI",
    offer2Desc:
      "Turn institutional knowledge and unstructured documents into reliable answers. Retrieval-grounded systems with source attribution, evaluation, and accuracy thresholds your teams can rely on.",
    offer3Title: "Predictive AI",
    offer3Desc:
      "Quantify what drives revenue, retention, and margin. Production models for churn, LTV, demand, recommendation, propensity, and profitability, deployed into the workflows where decisions happen.",
    offer4Title: "Data Platforms",
    offer4Desc:
      "Build the data foundation your AI and analytics depend on. Source-of-truth architecture, semantic layer, quality controls, and AI-readiness instrumentation engineered for trust.",
    domainsLabel: "What we build",
    domainsTitle: "Systems we deploy.\n",
    domainsSubtitle:
      "Production AI, data, and decision systems for the workflows standard software cannot fully address.",
    domain1Label: "AI agents and workflow automation",
    domain1Desc:
      "Reduce manual throughput costs in operations, finance, and internal execution. Bounded agents with routing logic, controls, fallback paths, and human review at the points that matter.",
    domain2Label: "Customer support and service systems",
    domain2Desc:
      "Lift resolution speed and contain cost-per-ticket without compromising quality. Intent classification, knowledge-grounded responses, agent assist, and confidence-based escalation.",
    domain3Label: "Knowledge systems and enterprise retrieval",
    domain3Desc:
      "Recover the hours your teams lose searching for answers. Retrieval-augmented assistants that reason across policies, SOPs, product data, and commercial documentation with source attribution.",
    domain4Label: "Document intelligence",
    domain4Desc:
      "Convert unstructured documents into reliable structured data. Extraction, classification, validation, and confidence scoring across contracts, proposals, invoices, and RFPs.",
    domain5Label: "Predictive models and decision science",
    domain5Desc:
      "Move commercial decisions from instinct to scored signal. Production models for churn, LTV, lead scoring, demand forecasting, and prioritization, deployed into the workflows where action happens.",
    domain6Label: "Recommendation and personalization",
    domain6Desc:
      "Lift conversion, basket size, and lifecycle revenue. Recommendation engines and next-best-action logic for ecommerce, merchandising, and customer experience workflows.",
    domain7Label: "Profitability and growth decision systems",
    domain7Desc:
      "Allocate capital against profit truth, not platform metrics. Measurement and modeling systems that connect channel, cohort, contribution margin, and payback economics for leadership.",
    domain8Label: "Data platforms for AI",
    domain8Desc:
      "Give every model and dashboard the same version of the truth. Source-of-truth architecture, semantic layer, QA, and instrumentation engineered for trust and traceability.",
    domain9Label: "AI operations, evaluation, and control",
    domain9Desc:
      "Keep production AI safe, observable, and improving. Monitoring, evaluation harnesses, prompt standards, human-in-the-loop controls, escalation logic, and operational safeguards.",
    founderLabel: "Founder",
    founderName: "Guillaume Belisle",
    founderRole: "Founder, Vert AI",
    founderP1:
      "Data scientist, ML engineer, and AI systems builder. Guillaume's founder experience includes designing and shipping production AI, ML, and data systems in operator environments including Louis Vuitton, Canada Goose, Lowe's, and Home Hardware.",
    founderP2:
      "He built Vert AI because the industry separates the people who think from the people who build. We do not. The person who frames the problem also architects the system, writes the code, and ships it.",
    founderP3: "One point of contact. No layers. No subcontractors.",
    founderTags: ["AI systems", "Data engineering", "ML / DS", "Decision systems", "Profitability measurement"],
    insightsLabel: "Insights",
    insightsTitle: "Thinking that ships.",
    allInsights: "All insights",
    insightRead: "Read implementation guide",
    insight1Cat: "AI Systems",
    insight1Title: "Why enterprise AI projects fail before production.",
    insight1Desc:
      "The pattern is rarely model choice. The bottleneck is workflow design, data architecture, ownership, controls, and production readiness.",
    insight2Cat: "Data Engineering",
    insight2Title: "The semantic layer is the most undervalued asset in enterprise AI.",
    insight2Desc:
      "Few organizations operate a governed semantic foundation. Without one, models and dashboards inherit competing versions of the truth.",
    insight3Cat: "Decision Science",
    insight3Title: "Your growth metrics are lying. Here is what to measure instead.",
    insight3Desc:
      "Revenue, ROAS, and blended CAC can hide margin leakage and cohort decay. Better measurement starts with contribution and payback.",
    faqLabel: "FAQ",
    faqTitle: "Straight answers.",
    faq1Q: "Strategy or implementation?",
    faq1A:
      "Both, by design. We frame the problem, architect the system, build it, and deploy it. One team, end to end. No handoff between the people who think and the people who build.",
    faq2Q: "What does production-grade mean?",
    faq2A:
      "The system runs inside your business. Not in a notebook. Not in a demo environment. Deployed, monitored, documented, and handed off with operating procedures and ownership.",
    faq3Q: "How is Vert AI different from other AI firms?",
    faq3A:
      "Technical depth and commercial judgment in one team. We do not build technology for its own sake. We build systems around measurable workflows, clear controls, and scoped deliverables.",
    faq4Q: "Who do you work with?",
    faq4A:
      "CEOs, COOs, CFOs, and data leaders at mid-market companies. Retail, ecommerce, professional services, franchise operators, and growth-stage businesses. Operators who want deployed systems, not AI theater.",
    faq5Q: "How are engagements priced?",
    faq5A:
      "Scoped to the problem. Priced against expected value. Every engagement has defined deliverables, acceptance criteria, and a fixed timeline. No retainers. No open-ended statements of work.",
    faq6Q: "Do you offer post-launch support?",
    faq6A:
      "Yes. The Operating Cadence covers refinement, retraining, evaluation, performance tuning, and roadmap expansion on a recurring basis.",
    ctaHeadline: "The next step is a conversation.",
    ctaDescription:
      "Describe the workflow, decision, or data bottleneck. We will tell you what we would build first, what needs to be true, and what a production path could cost.",
  },
  services: {
    heroLabel: "Services",
    heroTitle: "AI, data, and decision-system engagements built around operating value.",
    heroSubtitle:
      "Compare the paths Vert AI uses to move from workflow selection and data readiness to production systems with evaluation, human review, monitoring, and measurable impact.",
    coreLabel: "The five services",
    offer1Title: "Agentic AI",
    offer1Desc:
      "Bounded autonomous systems for operations, support, finance, and internal execution. Routing, controls, fallback paths, and human review at the points that matter.",
    offer2Title: "Generative AI",
    offer2Desc:
      "Retrieval-augmented systems that reason over institutional knowledge and unstructured documents. Grounded outputs, source attribution, and production-grade accuracy.",
    offer3Title: "Predictive AI",
    offer3Desc:
      "Production models that score, rank, forecast, and quantify economic truth. Churn, LTV, demand, recommendation, propensity, and profitability deployed into workflows.",
    offer4Title: "Data Platforms",
    offer4Desc:
      "The architecture, semantic layer, quality controls, and AI-readiness foundation required before AI, analytics, or decision systems can operate reliably.",
    howToChooseLabel: "How to choose",
    howToChooseTitle: "Start with the problem.",
    choose1Offer: "Production AI Systems",
    choose1Situation:
      "You have a concrete workflow or operating problem that needs a custom AI system built through deployment, evaluation, monitoring, human review, and measurement.",
    choose1Start: "Start here for custom production systems.",
    choose2Offer: "Agentic AI & Workflow Automation",
    choose2Situation:
      "A high-value workflow is consuming operating capacity, and you need agents or automations with tool use, controls, escalation, and human-in-the-loop design.",
    choose2Start: "Start here for autonomous workflows.",
    choose3Offer: "Data Platforms for AI",
    choose3Situation:
      "Reports disagree, AI initiatives stall, or the business needs governed entities, quality checks, semantic definitions, and activation layers before AI can work.",
    choose3Start: "Start here for data infrastructure.",
    choose4Offer: "Decision Intelligence",
    choose4Situation:
      "Recurring decisions are still made from dashboards, instinct, or fragmented signals, and need models, rules, thresholds, workflow logic, and feedback loops.",
    choose4Start: "Start here for recurring decisions.",
    choose5Offer: "AI Readiness & Opportunity Mapping",
    choose5Situation:
      "You have multiple AI ideas and need to rank workflows by value, feasibility, data gaps, implementation risk, and the first practical production roadmap.",
    choose5Start: "Start here before a build.",
    processLabel: "How we work",
    processTitle: "From problem to production.",
    step1Title: "Scope",
    step1Desc:
      "Define the workflow, owner, baseline, production risk, deliverables, success criteria, and timeline.",
    step2Title: "Architect",
    step2Desc: "System design, data flow, integration plan, and deployment path. Before a line of code is written.",
    step3Title: "Build",
    step3Desc:
      "Implementation against defined checkpoints. Data work, model and system development, evaluation, quality assurance, and iteration.",
    step4Title: "Deploy",
    step4Desc: "Production rollout, documentation, team enablement, monitoring setup, and operational handoff.",
    domainsLabel: "Implementation domains",
    domainsTitle: "Solutions these engagements deliver.",
    domainsSubtitle:
      "Each pillar builds specific AI, data, and decision systems inside the business. These are the implementation domains we operate in.",
    domainsGroup1: "Agentic AI",
    domainsGroup2: "Generative AI",
    domainsGroup3: "Predictive AI",
    domainsGroup4: "Data Platforms",
    domainsGroupCross: "Cross-cutting · Post-launch",
    domain1: "AI agents and workflow automation",
    domain2: "Customer support and service systems",
    domain3: "Knowledge systems and enterprise retrieval",
    domain4: "Document intelligence",
    domain5: "Predictive models and decision science",
    domain6: "Recommendation and personalization",
    domain7: "Profitability and growth decision systems",
    domain8: "Data platforms for AI",
    domain9: "AI operations, evaluation, and control",
    supportLabel: "After the build",
    supportTitle: "Ongoing support.",
    support1Title: "AI Operator Layer",
    support1Desc:
      "The controls that keep AI systems usable, monitored, and safe in production. Access controls, human-in-the-loop design, evaluation rules, escalation logic, monitoring, and operational SOPs.",
    support2Title: "Operating Cadence",
    support2Desc:
      "Post-launch refinement, retraining, evaluation, performance tuning, and roadmap expansion. Available in Core, Plus, and Elite tiers with monthly reviews.",
    ctaHeadline: "Map the first workflow worth building.",
    ctaDesc:
      "No deck required. Describe the workflow or decision bottleneck and we will outline the practical first production path.",
  },
  agenticAI: {
    backLink: "← Services",
    duration: "4–14 weeks",
    heroTitle: "Agentic AI",
    heroSubtitle:
      "Automate the high-value workflows that consume operating capacity. Bounded agent systems with routing, controls, fallback paths, and human review, shipped end to end.",
    heroTimeline: "Focused agent: 4–6 weeks · Integrated workflow: 6–10 weeks · Multi-agent system: 10–14 weeks",
    problemLabel: "The problem",
    problemP1:
      "Most agent demos collapse on first contact with real systems, real exceptions, and real accountability. The gap between a sandbox agent and one that operates safely inside a business is engineering, governance, and human-in-the-loop design, not model capability.",
    problemP2: "This engagement closes that gap with bounded autonomy, real controls, production deployment, and written acceptance criteria.",
    buyersLabel: "Built for",
    buyers: [
      "COO or Head of Operations whose manual workflows do not scale with growth",
      "CFO or finance leader automating routing, validation, and exception handling",
      "Head of Customer Operations who needs intelligent triage and resolution at volume",
      "Function leader moving from agent demos to a deployed, governed system",
    ],
    processLabel: "How it works",
    processTitle: "Six phases to production.",
    phases: [
      {
        num: "01",
        title: "Scope the workflow and frame the economics",
        desc: "Workflow selection, autonomy boundary definition, economic justification, success criteria, and ownership mapping. Output: a one-page workflow brief and acceptance criteria.",
      },
      {
        num: "02",
        title: "Design the agent architecture",
        desc: "End-to-end system design covering routing logic, tool and integration selection, control boundaries, fallback paths, and human-in-the-loop checkpoints. Output: an architecture diagram and tool inventory.",
      },
      {
        num: "03",
        title: "Build the orchestration",
        desc: "Tool wiring, prompt and orchestration design, business rules, exception handling, escalation logic, and operational guardrails. Output: a working agent against the agreed scope.",
      },
      {
        num: "04",
        title: "Evaluate and harden",
        desc: "Test cases, failure-mode analysis, behavioral scoring, business acceptance criteria, and reliability thresholds for autonomous behavior. Output: an evaluation report and reliability sign-off.",
      },
      {
        num: "05",
        title: "Deploy to production",
        desc: "Production rollout, access and permissions, monitoring requirements, audit trails, operational handoff, and full documentation. Output: a live system with monitoring in place.",
      },
      {
        num: "06",
        title: "Govern and adopt",
        desc: "User guidance, operating SOPs, escalation playbooks, and governance rules for safe, ongoing autonomous operation. Output: an adoption playbook and governance framework.",
      },
    ],
    useCasesLabel: "Common use cases",
    useCases: [
      "Operational workflow automation",
      "Customer support triage and resolution",
      "Internal request routing",
      "Finance and procurement automation",
      "Sales and operations copilots",
      "Compliance checks and validation",
      "Multi-step orchestration with human review",
      "Rules-and-LLM hybrid workflows",
      "Exception handling and escalation",
      "Bounded multi-agent systems",
    ],
    guaranteeLabel: "Delivery standard",
    guaranteeTitle: "Production agent delivery standard",
    guaranteeDesc:
      "The agreed scope defines the agent design, working implementation boundary, deployment path, review states, and operational controls before the build expands.",
    ctaHeadline: "Describe the workflow.",
    ctaDesc: "We scope the agent system, the controls, and the deployment path.",
  },
  generativeAI: {
    backLink: "← Services",
    duration: "4–12 weeks",
    heroTitle: "Generative AI",
    heroSubtitle:
      "Turn institutional knowledge and unstructured documents into reliable answers. Retrieval-grounded systems with citations, evaluation harnesses, and accuracy thresholds engineered for production.",
    heroTimeline: "Focused build: 4–6 weeks · Multi-source: 6–9 weeks · Enterprise deployment: 9–12 weeks",
    problemLabel: "The problem",
    problemP1:
      "Generative AI without grounding is a liability. Hallucinated answers, missing citations, and unreliable extraction have killed more enterprise pilots than any technical limitation. The systems that work anchor every answer to a verifiable source and are evaluated against business accuracy standards, not generic benchmarks.",
    problemP2: "This engagement builds those systems. Retrieval-grounded. Source-attributed. Production-evaluated.",
    buyersLabel: "Built for",
    buyers: [
      "Head of Operations whose teams lose hours inside policies, SOPs, and product data",
      "Legal, finance, or procurement leader processing high volumes of contracts and documents",
      "Head of Customer Experience who needs grounded, attributable assistance for agents",
      "Knowledge or learning leader building internal assistants for staff",
    ],
    processLabel: "How it works",
    processTitle: "Six phases to grounded systems.",
    phases: [
      {
        num: "01",
        title: "Scope the use case and map the sources",
        desc: "Knowledge or document scope defined, source inventory, accuracy targets, retrieval strategy, and ownership mapping. Output: a source map and accuracy specification.",
      },
      {
        num: "02",
        title: "Design the retrieval architecture",
        desc: "Ingestion design, chunking and embedding strategy, retrieval and reranking logic, grounding rules, and source-attribution model. Output: a retrieval architecture and evaluation plan.",
      },
      {
        num: "03",
        title: "Design generation and reasoning",
        desc: "Prompt and reasoning structure, citation enforcement, refusal and uncertainty handling, output schemas, and validation logic. Output: a working generation layer with citation enforcement.",
      },
      {
        num: "04",
        title: "Evaluate accuracy and citations",
        desc: "Gold-set evaluation, accuracy and citation scoring, failure-mode analysis, business acceptance thresholds, and continuous-feedback design. Output: an evaluation harness and accuracy sign-off.",
      },
      {
        num: "05",
        title: "Deploy to production",
        desc: "Production rollout, access controls, monitoring for drift and accuracy, operational handoff, and full documentation. Output: a live system with drift monitoring in place.",
      },
      {
        num: "06",
        title: "Adopt and improve continuously",
        desc: "User guidance, content-gap reporting, feedback loops, and a maintenance cadence for the underlying knowledge sources. Output: an improvement loop and content-ownership model.",
      },
    ],
    useCasesLabel: "Common use cases",
    useCases: [
      "Internal knowledge assistants",
      "Policy and SOP retrieval",
      "Contract and clause extraction",
      "Invoice and document processing",
      "RFP response generation",
      "Sales and product knowledge assistants",
      "Compliance documentation review",
      "Technical support knowledge systems",
      "Multi-source enterprise search",
      "Conversational interfaces over private data",
    ],
    guaranteeLabel: "Delivery standard",
    guaranteeTitle: "Grounded output delivery standard",
    guaranteeDesc:
      "The agreed scope defines retrieval coverage, citation behavior, evaluation cases, accuracy thresholds, and launch acceptance criteria before release.",
    ctaHeadline: "Show us the knowledge problem.",
    ctaDesc: "We scope the retrieval system, the accuracy targets, and the deployment path.",
  },
  predictiveAI: {
    backLink: "← Services",
    duration: "4–14 weeks",
    heroTitle: "Predictive AI",
    heroSubtitle:
      "Quantify what drives revenue, retention, and margin, and put that signal where decisions happen. Production models for churn, LTV, demand, recommendation, and profitability, shipped end to end.",
    heroTimeline: "Focused model: 4–6 weeks · Multi-model system: 6–10 weeks · Production deployment: 10–14 weeks",
    problemLabel: "The problem",
    problemP1:
      "Most analytics stays descriptive. Companies invest in reporting and never build the predictive systems that improve the decisions driving revenue, margin, and growth. The models that exist sit in notebooks, never reach the workflows where decisions happen, and are never evaluated against business outcomes.",
    problemP2: "This engagement builds models that drive action. Scored, deployed, and connected to the decisions that compound profit.",
    buyersLabel: "Built for",
    buyers: [
      "CEO or Founder who needs sharper commercial intelligence to guide capital and growth decisions",
      "CFO or CMO sizing churn, LTV, payback, and channel-mix decisions",
      "VP Growth or Head of Data Science who needs production-grade models, not notebooks",
      "Merchandising, ecommerce, or operations leader optimizing high-volume decisions",
    ],
    processLabel: "How it works",
    processTitle: "Six phases to decision utility.",
    phases: [
      {
        num: "01",
        title: "Frame the decision and the economics",
        desc: "Target decision defined, success metric established, economic value framed, and use case selected. Includes profitability and capital-allocation framings where relevant. Output: a one-page decision brief.",
      },
      {
        num: "02",
        title: "Design the model and features",
        desc: "Model approach selection, feature engineering, label design, validation strategy, and accuracy-versus-usability tradeoffs. Output: a feature spec and validation plan.",
      },
      {
        num: "03",
        title: "Train and evaluate",
        desc: "Train and validation framework, performance evaluation, business relevance checks, threshold and segmentation logic, and error analysis. Output: a validated model with documented performance.",
      },
      {
        num: "04",
        title: "Design the deployment",
        desc: "Scoring logic, production usage pattern, handoff into workflow or decision layer, retraining schedule, and monitoring. Output: a deployment design with retraining cadence.",
      },
      {
        num: "05",
        title: "Translate into business interpretation",
        desc: "Score meaning, action framework, downstream usage guidance, caveats, and reliability notes for operators. Output: a score-to-action playbook.",
      },
      {
        num: "06",
        title: "Integrate into the decision layer",
        desc: "Connection to the workflow, dashboard, or playbook where the score is acted on. For profitability work, this includes cut, scale, or fix recommendation logic. Output: a live decision system in the workflow.",
      },
    ],
    useCasesLabel: "Common use cases",
    useCases: [
      "Churn prediction and intervention",
      "Customer lifetime value modeling",
      "Lead and account scoring",
      "Demand and inventory forecasting",
      "Recommendation and next-best-action",
      "Propensity and segmentation models",
      "Pricing and assortment support",
      "Profitability and cohort economics",
      "Channel and payback measurement",
      "Operational classification and prioritization",
    ],
    guaranteeLabel: "Delivery standard",
    guaranteeTitle: "Decision utility delivery standard",
    guaranteeDesc:
      "The agreed scope defines the target decision, model or scoring boundary, interpretation rules, deployment design, and baseline metrics before launch.",
    ctaHeadline: "Name the decision.",
    ctaDesc: "We will scope the model, the data requirements, and the deployment path.",
  },
  dataPlatforms: {
    backLink: "← Services",
    duration: "4–14 weeks",
    heroTitle: "Data Platforms",
    heroSubtitle:
      "The data foundation your AI and analytics depend on. Source-of-truth architecture, semantic layer, quality controls, and AI-readiness instrumentation engineered for trust and traceability.",
    heroTimeline: "Focused: 4–6 weeks · Multi-source: 6–10 weeks · Broader environment: 10–14 weeks",
    problemLabel: "The problem",
    problemP1:
      "AI and analytics investments collapse when the data underneath is fragmented or untrusted. Models trained on unreliable data produce unreliable decisions. Generative systems retrieve the wrong context. Predictive models train on broken features. Reports give different answers depending on who runs them.",
    problemP2: "This engagement builds the trust layer your AI, analytics, and decision stack depend on.",
    buyersLabel: "Built for",
    buyers: [
      "COO or CFO who needs trustworthy reporting and decision-grade data",
      "Head of Data or analytics leader accountable for data quality and reliability",
      "RevOps or Marketing Ops leader with measurement gaps they cannot close",
      "Technical owner preparing data infrastructure for AI, ML, or generative deployment",
    ],
    processLabel: "How it works",
    processTitle: "Six phases to decision-grade data.",
    phases: [
      {
        num: "01",
        title: "Map the architecture and the sources",
        desc: "Source-system inventory, target architecture, source-of-truth design, entity mapping, and ownership structure. Output: a source map and target architecture diagram.",
      },
      {
        num: "02",
        title: "Model the data and the semantic layer",
        desc: "Core data models, semantic logic, metric definitions, transformation plan, and business-rule implementation. Output: implemented data models and a metric dictionary.",
      },
      {
        num: "03",
        title: "Implement quality and trust controls",
        desc: "QA checks, reconciliation rules, taxonomy cleanup, identity and duplication logic, and trust-gap remediation. Output: a quality test suite and reconciliation reports.",
      },
      {
        num: "04",
        title: "Instrument the event layer",
        desc: "Event layer design, process-stage capture, handoff tracking, exception logging, and traceability. Output: an event schema and instrumentation in production.",
      },
      {
        num: "05",
        title: "Prepare the AI and ML readiness layer",
        desc: "Feature availability, retrieval-readiness for generative systems, decision-grade vs. directional-grade classification, and upstream blocker identification. Output: an AI-readiness assessment and feature catalog.",
      },
      {
        num: "06",
        title: "Document and hand off",
        desc: "KPI dictionary, logic documentation, architecture notes, maintenance guidance, and roadmap for downstream AI systems. Output: a documentation set and downstream roadmap.",
      },
    ],
    useCasesLabel: "Common problems we solve",
    useCases: [
      "Disconnected source systems",
      "Inconsistent KPI logic across teams",
      "Weak source-of-truth structure",
      "Poor feature availability for ML and AI",
      "Broken reporting trust",
      "Missing instrumentation and event capture",
      "Unreliable input data for models",
      "No production-ready data models",
      "Retrieval gaps for generative systems",
    ],
    guaranteeLabel: "Delivery standard",
    guaranteeTitle: "Decision-grade data delivery standard",
    guaranteeDesc:
      "The agreed scope defines the source-of-truth structure, core data logic, quality checks, ownership model, and documentation required for acceptance.",
    ctaHeadline: "Start with the data.",
    ctaDesc: "Describe your data landscape. We scope the architecture and the path to reliability.",
  },
  about: {
    heroLabel: "About Vert AI",
    heroTitle: "Built for the gap between AI ambition and production reality.",
    heroSubtitle:
      "Vert AI helps operators turn high-value workflows, messy data, and decision bottlenecks into production AI systems with review paths, ownership, and measurable operating value.",
    secondaryCta: "Explore production AI systems",
    heroProof: [
      {
        label: "Production AI, not prototypes",
        desc: "Work is scoped around real workflows, data readiness, evaluation, monitoring, and adoption.",
      },
      {
        label: "Founder-led delivery",
        desc: "Founder-led scoping and senior technical involvement stay close to architecture, build quality, and production decisions.",
      },
      {
        label: "Measured before build",
        desc: "Baseline metrics, operating constraints, and success criteria are defined before implementation expands.",
      },
    ],
    whyExistsLabel: "Why Vert exists",
    whyExistsTitle: "Most companies do not need more AI experiments.",
    whyExistsP1:
      "They need better decisions, cleaner workflows, reliable data foundations, and systems that can be monitored after launch.",
    whyExistsP2:
      "Vert AI exists for operators who have moved past AI inspiration and need implementation discipline: choose the right workflow, expose the data gaps, define the baseline, build the system, and keep it accountable in production. Not every workflow should be automated.",
    principlesLabel: "Principles",
    principlesTitle: "How Vert AI decides what should be built.",
    principles: [
      {
        title: "Start with expensive workflows, not technology demos.",
        desc: "The first question is where speed, quality, cost, or decision accuracy is already creating measurable pressure.",
      },
      {
        title: "Define the baseline before building.",
        desc: "Cycle time, exception rate, review effort, error cost, adoption, and decision quality need a starting point.",
      },
      {
        title: "Make data readiness visible.",
        desc: "Source quality, ownership, permissions, semantics, and missing instrumentation are surfaced before they become production risk.",
      },
      {
        title: "Keep humans in control where risk, judgment, or customer impact matters.",
        desc: "AI should route, recommend, draft, retrieve, and score with clear review paths where accountability, privacy, or customer experience matters.",
      },
      {
        title: "Design for production from day one.",
        desc: "Evaluation, observability, fallback logic, access control, and operational ownership are part of the system design.",
      },
      {
        title: "Measure adoption and operating impact.",
        desc: "A system is not successful because it launched. It is successful when people use it and the workflow improves.",
      },
      {
        title: "Expand only after trust is earned.",
        desc: "The next workflow should come after the first one proves reliability, adoption, and business usefulness.",
      },
    ],
    differenceLabel: "What makes the work different",
    differenceTitle: "Strategy, data, workflow, and production engineering stay connected.",
    differenceItems: [
      {
        title: "Strategy and build in one team.",
        desc: "The people shaping the recommendation stay close to architecture, implementation, and launch constraints.",
      },
      {
        title: "AI + data + workflow + decision logic.",
        desc: "Useful AI usually depends on clean data paths, explicit rules, usable interfaces, and escalation design.",
      },
      {
        title: "Production controls included.",
        desc: "Evaluation, monitoring, auditability, human review, and fallback behavior are treated as core scope.",
      },
      {
        title: "Clear ownership and governance.",
        desc: "Every system needs accountable owners for data, logic, review, performance, and change management.",
      },
    ],
    trustLabel: "Trust and operating discipline",
    trustTitle: "Trust is designed into the system, not added at launch.",
    trustIntro:
      "Production AI has to be inspectable. Vert AI treats evaluation, monitoring, escalation, data quality, privacy, security review, ownership, adoption, and ROI measurement as implementation requirements, not slideware.",
    trustItems: [
      {
        title: "Evaluation",
        desc: "Test sets, acceptance criteria, edge cases, and review loops are defined so the system can be judged honestly.",
      },
      {
        title: "Monitoring",
        desc: "Usage, quality, latency, drift, failure modes, and business metrics need visibility after launch.",
      },
      {
        title: "Escalation logic",
        desc: "Confidence thresholds, exception routing, and human review paths keep judgment where it belongs.",
      },
      {
        title: "Data quality",
        desc: "Inputs, transformations, definitions, and ownership are made explicit so the system does not inherit hidden ambiguity.",
      },
      {
        title: "Privacy and security",
        desc: "Access, permissions, sensitive data handling, vendor exposure, and security review are considered before workflows are automated.",
      },
      {
        title: "Governance",
        desc: "Change control, documentation, ownership, and review cadence keep the system usable as the business changes.",
      },
    ],
    founderLabel: "Founder",
    founderName: "Guillaume Belisle",
    founderRole: "Founder, Vert AI",
    founderTitle: "Founder-led delivery, with senior technical involvement close to the work.",
    founderP1:
      "Guillaume Belisle is a data scientist, ML engineer, and AI systems builder. His founder experience includes designing and shipping production AI, ML, and data systems in operator environments including Louis Vuitton, Canada Goose, Lowe's, Home Hardware, and other organizations where failed implementation carries real operating cost.",
    founderP2:
      "Vert AI is built for direct senior involvement: scoping the workflow, pressure-testing the data foundation, shaping the system architecture, and reviewing what gets shipped.",
    founderP3:
      "The operating bias is commercial and technical at the same time. If a system cannot be measured, governed, adopted, and used with confidence by the people relying on it, it is not ready.",
    founderP4:
      "The same operating discipline carries into new work: scoped workflows, clear baselines, evaluation, observability, human review, governance, and business measurement.",
    ctaHeadline: "Bring the workflow that needs to become measurable.",
    ctaDesc:
      "Use the fit call to pressure-test whether the opportunity is ready for a production AI system, a data foundation pass, a narrower workflow automation, or no build yet.",
  },
  insights: {
    heroLabel: "Insights",
    heroTitle: "Thinking that ships.",
    heroSubtitle: "Buyer guides and field notes for operators evaluating production AI systems, data foundations, workflow controls, and measurable decision quality.",
    featured: "Featured",
    readArticle: "Read the operating guide",
    articles: [
      {
        cat: "Production AI",
        title: "Why AI pilots fail before they reach production",
        desc: "AI pilots usually fail before production because the workflow, data, owner, controls, integration path, and baseline were never made explicit. A practical operating diagnosis for teams trying to move from proof of concept to deployed capability.",
        date: "July 2026",
        readTime: "8 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI pilots fail, production AI, AI proof of concept, AI deployment readiness, AI workflow ownership, AI production controls",
        tags: ["Production AI", "AI Pilots", "Workflow Design", "Data Readiness", "AI Governance"],
      },
      {
        cat: "AI Readiness",
        title: "The production AI readiness checklist for mid-market companies",
        desc: "A practical readiness checklist for mid-market operators: workflow value, decision frequency, data availability, quality, ownership, risk, human review, integration feasibility, and measurement baseline.",
        date: "July 2026",
        readTime: "9 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI readiness checklist, mid-market AI, production AI readiness, AI opportunity assessment, AI workflow checklist",
        tags: ["AI Readiness", "Mid-market", "Production AI", "Workflow Assessment", "AI Scorecard"],
      },
      {
        cat: "Decision Systems",
        title: "Decision intelligence vs business intelligence",
        desc: "Business intelligence explains what happened. Decision intelligence helps operators decide what to do next, with data, rules, AI, workflow logic, feedback, and ownership designed into the system.",
        date: "July 2026",
        readTime: "8 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "decision intelligence vs business intelligence, BI vs decision systems, decision engine, operational analytics, AI decision systems",
        tags: ["Decision Intelligence", "Business Intelligence", "Decision Systems", "Analytics", "Workflow"],
      },
      {
        cat: "Workflow Automation",
        title: "How to choose the first AI workflow to automate",
        desc: "The first AI workflow should be valuable, frequent, bounded, measurable, reviewable, and connected to usable data. A selection framework for avoiding expensive automation theatre.",
        date: "July 2026",
        readTime: "8 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI workflow automation, choose AI use case, first AI workflow, workflow automation criteria, AI opportunity selection",
        tags: ["Workflow Automation", "AI Use Cases", "AI Readiness", "Operating Leverage", "Production AI"],
      },
      {
        cat: "AI Governance",
        title: "AI governance for operators: monitoring, evaluation, and human review",
        desc: "AI governance for operators is not committee theatre. It is the operating layer of monitoring, evaluation, escalation, human review, data quality, security, ownership, and rollback.",
        date: "July 2026",
        readTime: "9 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI governance for operators, AI monitoring, AI evaluation, human review AI, AI escalation, production AI governance",
        tags: ["AI Governance", "Monitoring", "Evaluation", "Human Review", "Production Controls"],
      },
      {
        cat: "Agentic AI",
        title: "What agentic AI should and should not automate",
        desc: "Agentic AI should automate bounded coordination, routing, drafting, retrieval, classification, and follow-up. It should not own high-risk judgment, policy exceptions, or irreversible commercial decisions without human control.",
        date: "July 2026",
        readTime: "8 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "agentic AI automation, AI agents workflow automation, what AI agents should automate, human in the loop agents, AI agent governance",
        tags: ["Agentic AI", "AI Agents", "Workflow Automation", "Human Review", "Governance"],
      },
      {
        cat: "Data Platforms",
        title: "The data foundation behind useful AI systems",
        desc: "Useful AI depends on a data foundation with source ownership, semantic definitions, quality checks, access controls, freshness, lineage, and activation paths into the workflow.",
        date: "July 2026",
        readTime: "8 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI data foundation, AI-ready data, data platforms for AI, semantic layer, data quality for AI, production AI data",
        tags: ["Data Platforms", "AI-ready Data", "Semantic Layer", "Data Quality", "Production AI"],
      },
      {
        cat: "AI Economics",
        title: "How to measure ROI before building an AI system",
        desc: "AI ROI should be measured before build by defining the current baseline, target movement, adoption path, review cost, implementation cost, and the operating metric the system must improve.",
        date: "July 2026",
        readTime: "8 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI ROI measurement, AI business case, AI baseline metrics, measure AI value, AI implementation ROI",
        tags: ["AI ROI", "Business Case", "Measurement", "AI Economics", "Baseline"],
      },
      {
        cat: "Decision Systems",
        title: "From dashboard to decision engine: what changes in production",
        desc: "A dashboard informs a human. A decision engine turns data into recommended, routed, monitored action. The production shift requires rules, thresholds, ownership, feedback, and controls.",
        date: "July 2026",
        readTime: "8 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "dashboard to decision engine, decision engine, decision intelligence, production decision systems, operational analytics",
        tags: ["Decision Engines", "Decision Intelligence", "Business Intelligence", "Workflow", "Production AI"],
      },
      {
        cat: "AI Strategy",
        title: "Why AI theatre is expensive",
        desc: "AI theatre is expensive because it consumes executive attention, data-team capacity, vendor budget, and operator trust without improving a workflow, decision, baseline, or measurable business outcome.",
        date: "July 2026",
        readTime: "7 min read",
        author: "Guillaume Belisle / Vert AI",
        keywords: "AI theatre, AI strategy, failed AI projects, production AI value, AI operating leverage, AI demos",
        tags: ["AI Strategy", "AI Theatre", "Production AI", "Operating Leverage", "AI ROI"],
      },
      {
        cat: "AI Strategy",
        title: "The AI procurement trap: how 2026 vendor contracts mortgage your roadmap.",
        desc: "Four structural clauses in standard enterprise AI contracts look reasonable in legal review and become catastrophic in year two: egress fees, model-version lock, embedding portability, and AI feature bundling. The procurement checklist a senior operator should run before signing.",
        date: "May 2026",
        readTime: "9 min read",
        keywords: "AI vendor contract, AI vendor lock-in, enterprise AI procurement, AI MSA red flags, AI contract clauses, foundation model contracts, AI vendor evaluation, AI egress fees",
        tags: ["AI Procurement", "Vendor Strategy", "Enterprise AI", "AI Contracts", "Vendor Lock-in"],
      },
      {
        cat: "AI Operations",
        title: "Human-in-the-loop is not a safety feature. It is a P&L line.",
        desc: "Every AI-augmented workflow is a labor reallocation. The economics only work when review time, escalation rate, and confidence-threshold calibration are designed together. A three-variable HITL cost model, and where teams burn the productivity gain on review queues.",
        date: "April 2026",
        readTime: "8 min read",
        keywords: "human in the loop AI, AI review workflow, AI operations cost, HITL economics, AI confidence threshold, AI escalation rate, AI augmented workflow, AI labor model",
        tags: ["AI Operations", "Human in the Loop", "AI Economics", "Workflow Design", "Production AI"],
      },
      {
        cat: "AI Systems",
        title: "The evaluation gap: why most enterprise AI systems have no honest scorecard.",
        desc: "F1, BLEU, and user satisfaction are not commercial metrics. The four-layer evaluation architecture, offline, shadow, controlled rollout, and in-production drift, that separates demos from systems leadership can defend at the board level.",
        date: "April 2026",
        readTime: "8 min read",
        keywords: "AI evaluation framework, enterprise AI metrics, model monitoring, AI scorecard, shadow evaluation, AI drift detection, production AI evaluation, LLM evaluation",
        tags: ["AI Evaluation", "Enterprise AI", "Model Monitoring", "AI Operations", "Production AI"],
      },
      {
        cat: "AI Systems",
        title: "Build vs buy for AI has changed. Here is the 2026 decision framework.",
      desc: "Foundation models are commodities. Orchestration is the differentiator. A three-axis decision model, proprietary data leverage, workflow specificity, and defensibility horizon, for choosing between SaaS AI features, assembled stacks, and owned systems.",
        date: "March 2026",
        readTime: "7 min read",
        keywords: "build vs buy AI, enterprise AI strategy, AI decision framework, foundation models, AI stack assembly, proprietary AI, AI vendor evaluation, AI defensibility",
        tags: ["AI Strategy", "Build vs Buy", "Enterprise AI", "AI Architecture", "Vendor Selection"],
      },
      {
        cat: "AI Systems",
        title: "Why enterprise AI projects fail before production",
        desc: "The bottleneck is rarely model selection. It is workflow design, data architecture, operating ownership, controls, and readiness. What implementation-led teams do differently.",
        date: "March 2026",
        readTime: "8 min read",
      },
      {
        cat: "Data Engineering",
        title: "The semantic layer is the most undervalued asset in enterprise AI.",
        desc: "Few enterprises operate a governed semantic foundation. Without one, models and dashboards inherit different versions of the truth. Why the semantic layer matters before AI scales.",
        date: "February 2026",
        readTime: "7 min read",
      },
      {
        cat: "Decision Science",
        title: "Your growth metrics are lying. Here is what to measure instead.",
        desc: "Revenue is vanity. ROAS is a platform fiction. CAC ignores cohort decay. Most growth measurement frameworks quietly reward capital destruction. A decision-science approach to measuring what actually compounds.",
        date: "January 2026",
        readTime: "6 min read",
      },
      {
        cat: "AI Systems",
        title: "Agentic AI in production: why many deployments will stall.",
        desc: "Agentic AI adoption is moving faster than operating discipline. The governance, evaluation, and workflow gaps separate demos from deployed systems.",
        date: "December 2025",
        readTime: "9 min read",
      },
      {
        cat: "Data Engineering",
        title: "Your data is not ready for AI. Five diagnostic signals.",
        desc: "Before investing in models, run this diagnostic. Five structural signals, from pipeline fragility to semantic drift, that predict whether your data layer will enable AI or undermine it.",
        date: "November 2025",
        readTime: "5 min read",
      },
      {
        cat: "Profitability Intelligence",
        title: "The hidden cost layer behind AI ROI calculations.",
        desc: "Change management, data preparation, integration work, and operational overhead often sit outside the business case. How to build an honest cost model before launch.",
        date: "October 2025",
        readTime: "7 min read",
      },
      {
        cat: "AI Systems",
        title: "The gap between a trained model and a production system is an engineering problem.",
        desc: "A model in a notebook is not a system. Monitoring, versioning, fallback logic, and operational scaffolding separate a demo from a deployed capability, and most ML teams are not equipped to close that gap.",
        date: "September 2025",
        readTime: "6 min read",
      },
      {
        cat: "Decision Science",
        title: "Decision layers: the missing architecture between data and action.",
        desc: "Most companies have dashboards. Very few have decision systems. The architectural pattern that connects predictive models, business rules, and operational workflows into systems that generate action, not reports.",
        date: "August 2025",
        readTime: "7 min read",
      },
      {
        cat: "Data Engineering",
        title: "From ETL to AI-native: what the 2026 data stack actually looks like.",
        desc: "The modern data stack is dead. The AI-native data stack, real-time, context-rich, and semantically governed, is what production AI requires. An architectural blueprint for data teams building for agentic systems.",
        date: "July 2025",
        readTime: "8 min read",
      },
      {
        cat: "Profitability Intelligence",
        title: "Capital allocation is a data problem. Most companies solve it with opinions.",
        desc: "Where to spend, where to cut, where to scale. These are measurement questions, not just strategy questions. Data engineering gives capital allocation a cleaner operating base.",
        date: "June 2025",
        readTime: "6 min read",
      },
    ],
  },
  contact: {
    heroLabel: "Contact",
    heroTitle: "Bring the workflow. Leave with a practical next step.",
    heroSubtitle:
      "Share the workflow, decision, or data problem you want to improve. Vert AI will help determine whether the next step is a production AI system, data foundation, workflow automation, decision layer, or no build yet.",
    bookingLabel: "Primary next step",
    bookingTitle: "Book a production AI fit call",
    bookingDesc:
      "Use the fit call to test whether the opportunity is concrete enough to build, what data or workflow work may need to come first, and whether the first useful answer is not to automate yet.",
    bookingMeta: "Founder-led scoping conversation",
    fallbackBooking: "Share project context",
    howToReach: "Alternative contact",
    scopingCall: "Share project context if you are not ready to book.",
    scopingCallDesc:
      "Prefer email or want to share details first? Send the context below. Every submission is reviewed directly and answered with a practical next step.",
    emailLabel: "Email",
    locationLabel: "Location",
    location: "Montreal, Canada",
    responseTimeLabel: "Response time",
    responseTime: "Within one business day",
    formLabel: "Project qualification",
    formTitle: "Tell us which workflow is worth improving.",
    formIntro:
      "Share enough context to understand the workflow, decision, data foundation, constraints, and timing. If the right answer is not to build yet, Vert AI will say so.",
    formName: "Name",
    formNamePlaceholder: "Your name",
    formRole: "Your role",
    formRolePlaceholder: "Founder, COO, VP Operations, Data Lead...",
    formCompany: "Company or operating group",
    formCompanyPlaceholder: "Company name",
    formEmail: "Work email",
    formEmailPlaceholder: "you@company.com",
    formWebsite: "Website",
    formWebsitePlaceholder: "https://company.com",
    formMessage: "Workflow, decision, or data problem",
    formMessagePlaceholder:
      "Describe the workflow, decision, or data problem. Include what happens today, what breaks, and what improvement would matter.",
    formNeed: "Closest project path",
    formNeedPlaceholder: "Select the closest fit",
    formTimeline: "Decision timing",
    formTimelinePlaceholder: "Select timing",
    formCompanySize: "Operating context",
    formCompanySizePlaceholder: "Team size, revenue stage, number of locations, ticket volume, data scale, or existing systems...",
    formOptional: "Optional",
    formSubmit: "Send workflow context",
    formSending: "Sending context...",
    formSuccessTitle: "Context received.",
    formSuccessDesc:
      "Vert AI will review the workflow context and respond within one business day with a fit-call path, clarifying question, or no-build recommendation.",
    formErrorTitle: "The inquiry did not send.",
    formErrorDesc:
      "Try again, book a fit call, or email the same workflow context directly.",
    validationRequired: "Add this detail before sending.",
    validationEmail: "Enter a valid work email.",
    needOptions: [
      "AI workflow automation",
      "Data platform for AI",
      "Decision intelligence",
      "AI readiness/opportunity mapping",
      "Not sure yet",
    ],
    timelineOptions: ["Now", "This quarter", "Next 6 months", "Exploring"],
    reassuranceLabel: "Qualification reassurance",
    reassuranceTitle: "A serious first conversation, not a generic AI audit.",
    reassurance: [
      {
        title: "Founder-led first conversation",
        desc: "The initial review is close to the person responsible for technical direction, founder-led scoping, and delivery standards.",
      },
      {
        title: "No generic AI audit",
        desc: "The conversation starts with the workflow, decision, data, and economics, not a recycled checklist.",
      },
      {
        title: "Clear next steps",
        desc: "If there is a fit, the next step is a scoped path with assumptions, risks, required data, and expected deliverables.",
      },
      {
        title: "Practical recommendation",
        desc: "If the best answer is not to build yet, Vert AI will say that and identify what should be resolved first.",
      },
    ],
    faqLabel: "FAQ",
    faqTitle: "Common questions before a fit call.",
    faqs: [
      {
        q: "What happens after I submit?",
        a: "Vert AI reviews the workflow, data, timing, and fit signals. You receive a practical response within one business day with either a fit-call path, a clarifying question, or a recommendation not to build yet.",
      },
      {
        q: "Do we need a defined use case?",
        a: "No. A rough workflow, recurring decision, data bottleneck, or operational pain is enough to start. Vert AI can help determine whether it should become an AI, data, automation, or decision-intelligence project.",
      },
      {
        q: "Can you work with our existing stack?",
        a: "Usually, yes. The first step is understanding current systems, data access, integration constraints, security and privacy requirements, and who will own the workflow after launch.",
      },
      {
        q: "Do you sign NDAs?",
        a: "Yes, when confidentiality is required before detailed technical or operational context can be shared.",
      },
      {
        q: "Do you work with teams outside Canada?",
        a: "Yes. Vert AI is Canada-based and works with North American operators, including teams outside Canada.",
      },
    ],
  },
  work: {
    heroLabel: "Work patterns",
    heroTitle: "How production AI work becomes credible before it scales.",
    heroSubtitle:
      "Vert AI uses scoped work patterns, baselines, evaluation, monitoring, human review, and governance to turn AI, data, and decision opportunities into production systems buyers can inspect.",
    operatingLabel: "What counts as work",
    operatingTitle: "A shipped system, not a polished demo.",
    operatingIntro:
      "The work has to survive real data, real users, exceptions, handoffs, monitoring, and operating ownership. That is the bar for calling an engagement complete.",
    operatingPrinciples: [
      {
        title: "Start with the workflow and decision",
        desc: "We identify the operating moment where AI, data, or decision logic can change throughput, quality, margin, or speed.",
      },
      {
        title: "Design around production constraints",
        desc: "Data quality, integrations, privacy, security, review queues, escalation paths, and adoption are scoped before implementation starts.",
      },
      {
        title: "Evaluate before and after launch",
        desc: "The system is tested against expected behavior, failure modes, business criteria, and handoff requirements before it becomes operating capability.",
      },
      {
        title: "Leave buyers with operating evidence",
        desc: "Architecture, controls, documentation, ownership, and measurement are visible enough for leadership and operators to inspect.",
      },
    ],
    evidenceLabel: "Delivery evidence",
    evidenceTitle: "What a serious buyer should expect to see.",
    evidenceIntro:
      "Different engagements produce different artifacts, but production AI work should leave a paper trail that explains how the system works, how it is governed, and who owns it after launch.",
    deliverables: [
      {
        title: "Workflow and opportunity brief",
        desc: "A tight definition of the business problem, users, data inputs, decision points, constraints, success criteria, and expected operating value.",
      },
      {
        title: "Architecture and control plan",
        desc: "System design, data flow, tool boundaries, human review, fallback behavior, escalation logic, and risk controls.",
      },
      {
        title: "Evaluation and acceptance record",
        desc: "Test cases, observed failure modes, quality thresholds, business acceptance criteria, and launch readiness notes.",
      },
      {
        title: "Production handoff package",
        desc: "Documentation, monitoring expectations, ownership model, support cadence, and clear next-step recommendations after launch.",
      },
    ],
    founderLabel: "Founder experience",
    founderTitle: "Founder-led production experience.",
    founderP2:
      "Vert AI shows its work through methodology, delivery standards, and production patterns: scoped workflows, clear baselines, evaluation, monitoring, human review, governance, and measurable operating impact.",
    surfacesLabel: "Where to go next",
    surfacesTitle: "Choose the surface that matches your buying question.",
    surfaces: [
      {
        href: "/services",
        title: "Explore production AI systems",
        desc: "See the production AI, workflow automation, data platform, decision intelligence, and readiness engagements.",
      },
      {
        href: "/industries",
        title: "Explore industries served",
        desc: "Map Vert AI's work to retail, ecommerce, CPG, franchise, professional services, and B2B operating contexts.",
      },
      {
        href: "/insights",
        title: "Read implementation analysis",
        desc: "Use the buyer guides and field notes to evaluate production AI claims more sharply.",
      },
      {
        href: "/contact",
        title: "Book a production AI fit call",
        desc: "Describe the workflow, data challenge, or decision system you need to improve.",
      },
    ],
    ctaHeadline: "Bring the real workflow.",
    ctaDesc:
      "A fit call should clarify whether there is a production system worth building, what evidence would prove it, who would own it, and what has to be true before it ships.",
  },
  notFound: {
    title: "404",
    message: "Page not found.",
    backHome: "Return to Home",
  },
  footer: {
    tagline: "Production-grade AI, data, and decision systems. Designed and shipped from Canada.",
    engagements: "Engagements",
    services: "Services",
    industries: "Industries",
    insights: "Insights",
    company: "Company",
    contactLabel: "Contact",
    copyright: "Vert AI Inc.",
    bottomTagline: "AI, Data & Decision Systems · Canada",
  },
  solutionsHub: {
    heroLabel: "Solutions",
    heroTitle: "Production AI patterns for costly workflows.",
    heroSubtitle: "Use this catalogue to map documents, recurring decisions, workflow automation, and data-platform needs to the Vert AI service path, controls, ownership model, and first build that fit.",
    primaryLabel: "Solution categories",
    primaryTitle: "Review by operating problem.",
    primarySubtitle: "Each category maps a recurring operating problem to the system pattern, controls, data needs, and service path that fit.",
    secondaryLabel: "Additional capabilities",
    secondaryTitle: "Supporting domains.",
    recommendationTitle: "Recommendation and personalization",
    recommendationDesc: "Recommendation engines and next-best-action logic for ecommerce, merchandising, and customer experience.",
    recommendationVia: "Via Predictive AI",
    profitabilityGrowthTitle: "Profitability and growth decisions",
    profitabilityGrowthDesc: "Measurement and modeling systems connecting channel, cohort, and payback economics for capital allocation.",
    profitabilityGrowthVia: "Via Predictive AI",
    aiOpsTitle: "AI operations and control",
    aiOpsDesc: "Monitoring, QA, evaluation, escalation logic, and operational safeguards for production AI systems.",
    aiOpsVia: "Cross-cutting · Post-launch",
    bridgeLabel: "How to engage",
    bridgeTitle: "From problem to engagement.",
    bridgeDesc: "Every solution ships through the service path that fits the workflow, data foundation, decision layer, or readiness question. Scoped to your problem, priced against expected value, with deliverables, owners, controls, and acceptance criteria defined up front.",
    bridgeCta: "Explore production AI systems",
    ctaHeadline: "Map your first AI workflow.",
    ctaDesc: "We map the workflow, production risk, ownership, service path, and first build worth scoping.",
  },
  solutions: {
    aiAgents: {
      label: "Solution",
      heroTitle: "AI Agents and Workflow Automation",
      hubDesc: "Bounded AI workflows for operations, finance, and internal execution. With routing, controls, and human review.",
      heroSubtitle: "Bounded AI workflows that automate high-value, repetitive processes only where the risk is controlled. Built with routing, controls, fallback paths, and human review at the points that matter.",
      problemLabel: "The problem",
      problemTitle: "Manual processes that do not scale.",
      problemP1: "High-value workflows in operations, finance, and internal execution are still run by hand. Volume is growing. Headcount is not. Generic automation handles the simple cases and breaks on exceptions, edge cases, and anything requiring judgment.",
      problemP2: "Vert AI builds bounded agents that handle the routine, escalate the exceptions, and keep humans in control where judgment, risk, or customer impact matters.",
      gapsLabel: "Why generic tools fall short",
      gaps: "Cannot handle multi-step reasoning or branching logic|No fallback or escalation when confidence drops|Cannot integrate across multiple internal systems|No audit trail or operational controls|Break on edge cases that require contextual judgment",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Multi-step AI agents for operational workflows|Routing and triage systems with confidence scoring|Document-triggered automation pipelines|Finance and procurement workflow automation|Internal copilots for functional teams|Rules-and-LLM hybrid orchestration|Exception handling and escalation logic|Human-in-the-loop review interfaces",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "Invoice processing and validation|Order management and exception handling|Compliance checking and document review|Employee onboarding automation|Internal request routing and triage|Supply chain communication automation|Audit preparation and evidence gathering",
      humanLabel: "Human oversight",
      humanTitle: "Humans stay in the loop.",
      humanDesc: "Every agent system includes configurable escalation thresholds, manual review queues, audit logs, and operational controls. Automation handles the volume. Humans handle the judgment. The system knows the difference.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Most often delivered through Agentic AI.",
      engagementDesc: "This solution is typically delivered through the Agentic AI engagement, which covers scoping, architecture, implementation, evaluation, controls, and production deployment with clear ownership.",
      engagementHref: "/services/agentic-ai-workflows",
      engagementCta: "Map your first AI workflow",
      ctaHeadline: "Describe the workflow.",
      ctaDesc: "We will scope the system, the timeline, and the deliverables.",
    },
    documentIntelligence: {
      label: "Solution",
      heroTitle: "Document Intelligence",
      hubDesc: "Extraction, classification, and structured output from contracts, invoices, RFPs, and high-friction document workflows.",
      heroSubtitle: "Extraction, classification, validation, review queues, and structured output from contracts, proposals, invoices, RFPs, and other high-friction document workflows.",
      problemLabel: "The problem",
      problemTitle: "Documents that trap value.",
      problemP1: "Critical business information is locked inside unstructured documents. Contracts, proposals, invoices, and regulatory filings hold the data your systems need, but extracting it reliably at scale is a problem generic OCR and template-based tools cannot solve.",
      problemP2: "Vert AI builds document intelligence systems that extract, classify, validate, review, and structure information from complex documents with production controls.",
      gapsLabel: "Why generic tools fall short",
      gaps: "Template-based extraction breaks on format variation|OCR alone cannot handle semantic understanding|No validation or confidence scoring|Cannot map extracted data to business entities|No feedback loop for continuous improvement",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Contract analysis and clause extraction|Invoice processing and validation systems|RFP response automation|Regulatory document classification|Insurance claims processing|Proposal comparison and scoring|Document-to-database pipelines|Multi-format extraction with confidence scoring",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "Procurement contract review|Accounts payable automation|Legal document analysis|Insurance underwriting document processing|Compliance document classification|Proposal intake and scoring|Regulatory filing extraction",
      humanLabel: "Human oversight",
      humanTitle: "Humans validate what matters.",
      humanDesc: "Every document intelligence system includes confidence thresholds, manual review queues for low-confidence extractions, and validation rules aligned with business logic. The system handles volume. Humans handle exceptions and edge cases.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Usually delivered through Generative AI.",
      engagementDesc: "Document intelligence solutions are typically delivered through the Generative AI engagement. For organizations that also need to restructure their data foundation, the Data Platforms engagement may apply.",
      engagementHref: "/services/production-ai-systems",
      engagementCta: "Explore grounded document systems",
      ctaHeadline: "Show us the documents.",
      ctaDesc: "We will scope the extraction system, the accuracy targets, and the deployment path.",
    },
    knowledgeSystems: {
      label: "Solution",
      heroTitle: "Knowledge Systems and Enterprise Retrieval",
      hubDesc: "Internal assistants that retrieve, reason over, and operationalize institutional knowledge across your organization.",
      heroSubtitle: "Internal assistants that retrieve, reason over, and operationalize institutional knowledge across policies, SOPs, product data, and commercial documentation with source attribution and feedback loops.",
      problemLabel: "The problem",
      problemTitle: "Knowledge that exists but cannot be found.",
      problemP1: "Your organization has the answers. They are scattered across hundreds of documents, wikis, SOPs, and legacy systems. Employees lose hours searching for information that should take seconds. Generic search returns keywords, not answers.",
      problemP2: "Vert AI builds knowledge systems that retrieve relevant context, reason across approved sources, show citations, and make quality feedback visible.",
      gapsLabel: "Why generic search falls short",
      gaps: "Keyword search returns results, not answers|Cannot reason across multiple documents|No understanding of organizational context|Cannot handle follow-up questions or clarification|No source attribution or confidence scoring",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Enterprise knowledge assistants with RAG architecture|Policy and SOP retrieval systems|Product information retrieval for sales and support|Technical documentation assistants|Multi-source knowledge aggregation|Conversational interfaces with source attribution|Context-aware search with semantic understanding|Knowledge base curation and maintenance tooling",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "HR policy and benefits questions|Technical support knowledge retrieval|Sales enablement and product information|Regulatory compliance knowledge access|Onboarding and training knowledge delivery|Operations procedure lookup|Cross-department knowledge sharing",
      humanLabel: "Human oversight",
      humanTitle: "Accuracy you can verify.",
      humanDesc: "Every knowledge system includes source attribution, confidence indicators, and feedback mechanisms. Users can verify where answers come from. Administrators can track accuracy, identify knowledge gaps, and improve the system continuously.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Generative AI.",
      engagementDesc: "Knowledge systems are delivered through the Generative AI engagement. The build covers retrieval architecture design, ingestion pipeline, reasoning logic, and production deployment with monitoring.",
      engagementHref: "/services/production-ai-systems",
      engagementCta: "Explore governed knowledge systems",
      ctaHeadline: "Describe the knowledge problem.",
      ctaDesc: "We will scope the retrieval system, the sources, and the deployment path.",
    },
    customerSupport: {
      label: "Solution",
      heroTitle: "Customer Support and Service Systems",
      hubDesc: "AI systems that resolve requests, assist agents, classify intent, and improve speed without sacrificing quality.",
      heroSubtitle: "AI systems that resolve repetitive requests, assist agents, classify intent, surface the right knowledge, and improve speed without sacrificing quality, privacy, or oversight.",
      problemLabel: "The problem",
      problemTitle: "Support that does not scale.",
      problemP1: "Ticket volume grows. Agent capacity does not. Repetitive questions consume experienced agents. Response times drift. Quality becomes inconsistent. Generic chatbots handle only the simplest cases and frustrate customers on anything beyond FAQ-level queries.",
      problemP2: "Vert AI builds support systems that handle the routine with precision, assist agents on complex cases, and escalate when human judgment or customer impact requires review.",
      gapsLabel: "Why generic chatbots fall short",
      gaps: "Only handle FAQ-level questions|No integration with internal knowledge or systems|Cannot assist human agents on complex cases|No intent classification or routing logic|No learning from resolution patterns",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "AI-assisted ticket triage and routing|Automated resolution for common request types|Agent-assist with suggested responses|Intent classification and priority scoring|Knowledge-grounded response generation|Confidence-based escalation logic|Customer sentiment and urgency detection|Resolution quality monitoring and feedback loops",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "Tier-1 support automation|Order status and tracking inquiries|Returns and exchange processing|Account and billing questions|Product information requests|Service scheduling and coordination|Complaint classification and routing",
      humanLabel: "Human oversight",
      humanTitle: "Agents stay in control.",
      humanDesc: "Every support system includes configurable automation boundaries, agent override, and escalation thresholds. AI handles volume and consistency. Agents handle complexity and empathy. The system routes intelligently between the two.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Agentic AI.",
      engagementDesc: "Customer support systems are delivered through the Agentic AI engagement. Scope includes intent modeling, knowledge integration, agent-assist interfaces, and production deployment with quality monitoring.",
      engagementHref: "/services/agentic-ai-workflows",
      engagementCta: "Map your first AI workflow",
      ctaHeadline: "Describe the support challenge.",
      ctaDesc: "We will scope the system, the automation boundaries, and the deployment path.",
    },
    predictiveModels: {
      label: "Solution",
      heroTitle: "Predictive Models and Decision Science",
      hubDesc: "Custom models for churn, LTV, scoring, and demand forecasting. Built to rank, score, and guide action in production.",
      heroSubtitle: "Custom models for churn, LTV, lead scoring, demand forecasting, prioritization, and commercial decision support. Built to score, rank, explain, and guide action in production.",
      problemLabel: "The problem",
      problemTitle: "Decisions driven by instinct, not signal.",
      problemP1: "Most companies have dashboards that describe the past. Very few have systems that predict what happens next and recommend what to do about it. The models that exist live in notebooks. They never reach the workflows where decisions actually happen.",
      problemP2: "Vert AI builds predictive models that drive commercial action through deployed scoring, recommendation, review, and feedback systems.",
      gapsLabel: "Why dashboards fall short",
      gaps: "Descriptive analytics cannot predict outcomes|Models in notebooks never reach production|No integration with operational workflows|Scoring requires real-time or near-real-time deployment|No feedback loop between prediction and outcome",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Churn prediction and intervention models|Customer lifetime value modeling|Lead scoring and prioritization systems|Demand and inventory forecasting|Assortment and pricing optimization support|Propensity models for marketing and sales|Customer quality and segmentation scoring|Operational classification and prioritization",
      workflowsLabel: "Typical decisions",
      workflowsTitle: "Decisions we improve.",
      workflows: "Which customers are most likely to churn|Where to allocate sales effort|What inventory levels to maintain|Which leads to prioritize|How to segment customers by value|Where to adjust pricing|Which marketing cohorts to invest in",
      humanLabel: "Decision support, not replacement",
      humanTitle: "Models inform. Humans decide.",
      humanDesc: "Every model is built with interpretability, confidence scoring, monitoring, and a clear action framework. The system provides the signal. Your team applies the judgment. Decision thresholds are configurable and transparent.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Predictive AI.",
      engagementDesc: "Predictive models and decision science solutions are delivered through the Predictive AI engagement, which covers decision framing, model design, training, evaluation, deployment, and business interpretation.",
      engagementHref: "/services/decision-intelligence",
      engagementCta: "See how decision intelligence works",
      ctaHeadline: "Name the decision.",
      ctaDesc: "We will scope the model, the data requirements, and the deployment path.",
    },
    dataPlatforms: {
      label: "Solution",
      heroTitle: "Data Platforms for AI",
      hubDesc: "Source-of-truth, semantic, and QA layers required for reliable AI and consistent analytics in production.",
      heroSubtitle: "The source-of-truth, semantic, access-control, and QA layers required for reliable AI, consistent analytics, and production model deployment. Built for trust and traceability.",
      problemLabel: "The problem",
      problemTitle: "AI cannot run on untrusted data.",
      problemP1: "AI and analytics investments collapse when the data underneath is fragmented, inconsistent, or untrusted. Models trained on unreliable inputs produce unreliable decisions. Reports built on inconsistent logic erode confidence faster than they build it.",
      problemP2: "Vert AI builds the data foundation your AI and decision systems require before they can operate reliably.",
      gapsLabel: "Why existing data stacks fall short",
      gaps: "Fragmented sources with no single source of truth|Inconsistent metric definitions across teams|No semantic layer for AI model inputs|Missing data quality and trust controls|Pipeline fragility and lack of traceability",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Source-of-truth data architecture|Semantic layer and metric definitions|Data quality and trust controls|Event instrumentation and traceability|AI and ML readiness layers|Entity resolution and identity management|Pipeline reliability engineering|Documentation and governance frameworks",
      workflowsLabel: "Common data challenges",
      workflowsTitle: "Problems we solve.",
      workflows: "Reports give different answers depending on who runs them|AI models trained on inconsistent inputs|No single source of truth across systems|Missing instrumentation for key business events|Data quality issues blocking AI deployment|Teams cannot agree on metric definitions|Pipeline failures with no traceability",
      humanLabel: "Designed for trust",
      humanTitle: "Data your team can rely on.",
      humanDesc: "Every data platform includes documentation, governance rules, access logic, quality monitoring, and clear ownership mapping. The goal is not just clean data. It is trusted data that your organization and AI stack can depend on.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Data Platforms.",
      engagementDesc: "Data platform solutions are delivered through the Data Platforms engagement, which covers source mapping, data modeling, quality controls, instrumentation, AI readiness, and documentation.",
      engagementHref: "/services/data-platforms",
      engagementCta: "Explore AI-ready data foundations",
      ctaHeadline: "Start with the data.",
      ctaDesc: "We will scope the architecture and the path to reliability.",
    },
  },
  authority: {
    vetVendor: {
      label: "Strategic guide",
      heroTitle: "How to Vet an AI Vendor",
      heroSubtitle: "A decision framework for operators evaluating AI implementation partners. What to look for, what to avoid, and how to separate real capability from polished marketing.",
      readTime: "12 min read · Evergreen reference",
      thesisLabel: "The core problem",
      thesisTitle: "Most AI vendor evaluations miss what matters.",
      thesisP1: "Companies evaluating AI vendors tend to focus on credentials, case studies, and pricing. These are useful but insufficient. The variables that determine whether an AI project succeeds or fails are almost entirely about engineering capability, data maturity assessment, production readiness, and post-deployment support.",
      thesisP2: "This guide provides a practical framework for separating vendors who can deliver production systems from those who deliver presentations.",
      sectionsLabel: "Evaluation framework",
      sectionsTitle: "What to assess.",
      sections: "Production track record|Technical depth vs. breadth|Data assessment capability|Deployment and handoff model|Pricing and scope transparency|Post-launch support structure",
      sectionDescs: "Ask for systems running in production today. Demos and POCs are not evidence of delivery capability.|Specialists who go deep on fewer problems are easier to evaluate than generalists who claim to do everything. Ask about architecture decisions, not just outcomes.|A strong vendor assesses your data readiness before committing to a timeline. Weak vendors skip this step and blame the data later.|How does the system get into production? Who maintains it? If the answer is unclear, the project will stall at deployment.|Fixed scope with defined deliverables is easier to govern than open-ended retainers. Ask for written acceptance criteria.|AI systems require monitoring, retraining, and refinement. Ask what happens after launch.",
      takeawaysLabel: "Key signals",
      takeawaysTitle: "What separates strong vendors from weak ones.",
      takeaways: "They scope before they sell|They assess data readiness before committing to timelines|They build for production, not for demos|They define outputs and acceptance criteria, not just effort|They explain tradeoffs honestly|They have a clear post-launch model|They do not outsource core engineering",
      perspectiveLabel: "The Vert AI perspective",
      perspectiveTitle: "We built Vert AI to pass this test.",
      perspectiveDesc: "Every engagement has defined deliverables, acceptance criteria, and a single point of contact from scope to production. No subcontractors. No handoffs. No ambiguity about what gets delivered.",
      relatedLabel: "Explore further",
      relatedTitle: "Related solutions and resources.",
      relatedSolutions: "What production-grade AI actually means|Agentic AI engagement|All solutions",
      relatedHrefs: "/insights/production-grade-ai|/services/agentic-ai-workflows|/solutions",
      ctaHeadline: "Evaluating AI partners?",
      ctaDesc: "Talk through your requirements. We will give you an honest assessment of fit and what a production-ready engagement looks like.",
    },
    productionGrade: {
      label: "Technical reference",
      heroTitle: "What 'Production-Grade AI' Actually Means",
      heroSubtitle: "A technical definition for operators and decision-makers. What separates a working demo from a system that runs reliably inside a business.",
      readTime: "10 min read · Evergreen reference",
      thesisLabel: "The definition gap",
      thesisTitle: "Everyone claims production-grade. Few deliver it.",
      thesisP1: "The term 'production-grade' has been diluted by overuse. Vendors use it to describe anything from a Jupyter notebook with a Streamlit front-end to a fully monitored, fault-tolerant system with operational documentation. For buyers, this ambiguity is expensive.",
      thesisP2: "This page defines what production-grade actually requires, why most AI projects fail to reach it, and what to look for in a partner who can deliver it.",
      sectionsLabel: "Production requirements",
      sectionsTitle: "What production-grade demands.",
      sections: "Reliability and fault tolerance|Monitoring and observability|Data pipeline integrity|Security and access controls|Documentation and handoff|Operational support model",
      sectionDescs: "The system handles errors gracefully, retries intelligently, and degrades without catastrophic failure. It runs on schedules or triggers, not manual execution.|Performance metrics, quality scores, drift detection, and alerting. You know when the system degrades before your users do.|Input data is validated, versioned, and traceable. The system knows when its inputs change in ways that affect output quality.|Role-based access, audit trails, and compliance-ready architecture. Not afterthoughts bolted on before launch.|Complete technical documentation, operating procedures, and team enablement. The system is transferable, not dependent on the builder.|Monitoring, retraining, refinement, and roadmap support. Production is the beginning of the system's lifecycle, not the end.",
      takeawaysLabel: "Diagnostic checklist",
      takeawaysTitle: "Is your AI system production-grade?",
      takeaways: "It runs without manual intervention|It handles errors and edge cases gracefully|It is monitored with clear alerting|Its data inputs are validated and traceable|It has complete documentation|It has defined operational ownership|It has a retraining and refinement plan|It meets security and compliance requirements",
      perspectiveLabel: "The Vert AI standard",
      perspectiveTitle: "This is the bar Vert AI scopes against.",
      perspectiveDesc: "Every engagement is scoped against production readiness standards: deployment path, monitoring, documentation, operating handoff, and acceptance criteria before launch.",
      relatedLabel: "Explore further",
      relatedTitle: "Related solutions and resources.",
      relatedSolutions: "Agentic AI engagement|AI Agents and Workflow Automation|Data Platforms for AI",
      relatedHrefs: "/services/agentic-ai-workflows|/solutions/ai-agents-workflow-automation|/solutions/data-platforms-for-ai",
      ctaHeadline: "Need a production-grade system?",
      ctaDesc: "Describe the workflow. We will scope the system, the deployment path, and the production requirements.",
    },
    workflowsDeserveAI: {
      label: "Decision framework",
      heroTitle: "Which Workflows Deserve AI — and Which Don't",
      heroSubtitle: "Not every process needs AI. A practical framework for identifying where AI creates real value versus where it creates expensive complexity.",
      readTime: "9 min read · Evergreen reference",
      thesisLabel: "The selection problem",
      thesisTitle: "Most companies automate the wrong workflows first.",
      thesisP1: "The pressure to adopt AI leads companies to apply it everywhere without evaluating where it actually creates value. The result is a portfolio of expensive pilots that solve problems nobody prioritized and ignore the workflows that would generate the highest return.",
      thesisP2: "This framework helps operators identify which workflows deserve AI investment, which deserve simpler automation, and which should stay manual.",
      sectionsLabel: "Evaluation criteria",
      sectionsTitle: "How to assess workflow-AI fit.",
      sections: "Volume and repetition|Decision complexity|Data availability|Error cost and tolerance|Integration requirements|Measurement clarity",
      sectionDescs: "High-volume, repetitive workflows with consistent patterns are the strongest candidates. Low-volume, highly variable workflows rarely justify AI investment.|Workflows requiring multi-step reasoning, contextual judgment, or pattern recognition benefit from AI. Simple rule-based processes are better served by traditional automation.|AI requires structured, accessible, and sufficiently rich data. If the data does not exist or requires extensive cleanup, start with the data platform.|Workflows where errors are expensive but detectable are good candidates. Workflows where errors are catastrophic and undetectable require extreme caution.|Workflows that operate in isolation are easier to automate. Workflows deeply embedded in complex systems require more architectural planning.|If you cannot measure the improvement, you cannot justify the investment. Clear baseline metrics and success criteria are prerequisites.",
      takeawaysLabel: "Decision signals",
      takeawaysTitle: "When to invest in AI.",
      takeaways: "The workflow is high-volume and repetitive|The data exists and is reasonably structured|The error cost is manageable with human oversight|The improvement is measurable in business terms|The workflow has clear boundaries and handoff points|Standard software cannot handle the complexity|The team is ready to adopt and operate the system",
      perspectiveLabel: "How Vert AI approaches this",
      perspectiveTitle: "We start with the workflow, not the technology.",
      perspectiveDesc: "Every engagement begins with workflow selection and business framing. We help clients identify the highest-value automation targets before committing to a build. If AI is not the right answer, we say so.",
      relatedLabel: "Explore further",
      relatedTitle: "Related solutions and resources.",
      relatedSolutions: "AI Agents and Workflow Automation|Document Intelligence|Customer Support Systems|All solutions",
      relatedHrefs: "/solutions/ai-agents-workflow-automation|/solutions/document-intelligence|/solutions/customer-support-service-systems|/solutions",
      ctaHeadline: "Not sure where AI fits?",
      ctaDesc: "Describe your workflows. We will help you identify where AI creates real value and where it does not.",
    },
  },
  industriesHub: {
    heroLabel: "Industries",
    heroTitle: "Production AI systems for operational teams.",
    heroSubtitle: "See where AI can improve throughput, decision quality, service consistency, and data reliability across costly workflows, exceptions, and recurring decisions without losing review, ownership, or control.",
    listLabel: "Sectors we serve",
    listTitle: "Industry-specific workflow patterns.",
    listSubtitle: "We build for industries where standard software stops short, data quality matters, and failed implementation carries real operating cost.",
    bridgeLabel: "Cross-industry capabilities",
    bridgeTitle: "Same rigor. Different context.",
    bridgeDesc: "Every industry engagement draws from the same core solutions and delivery model. The architecture, data patterns, review paths, and operational requirements change. The engineering standard does not.",
    ctaHeadline: "Review the first workflow worth scoping.",
    ctaDesc: "We scope the system around your workflows, constraints, data environment, and production risk.",
  },
  industries: {
    accounting: {
      label: "Industry",
      heroTitle: "AI Systems for Accounting Firms",
      hubDesc: "Document processing, knowledge retrieval, and workflow automation built for the compliance, accuracy, and audit requirements of accounting practices.",
      heroSubtitle: "Production-grade AI systems for document processing, knowledge retrieval, client communication, and workflow automation. Built with review paths for the compliance, accuracy, and audit requirements of professional accounting.",
      painsLabel: "The challenge",
      painsTitle: "Where standard software stops short.",
      painsP1: "Accounting firms handle enormous volumes of structured and unstructured documents, repetitive client inquiries, and complex compliance requirements. Standard practice management software handles scheduling and billing but cannot automate the knowledge-intensive work that consumes senior staff time.",
      painsP2: "AI systems built for accounting workflows must meet accuracy, compliance, and audit-trail requirements that generic automation tools cannot satisfy.",
      painsListLabel: "Common workflow pain points",
      pains: "Manual document processing for tax returns and financial statements|Repetitive client inquiries consuming senior staff time|Knowledge trapped in partner expertise and institutional memory|Compliance checking that relies on individual knowledge|Inconsistent work product quality across team members|Seasonal capacity constraints with no scalable solution",
      systemsLabel: "What we build",
      systemsTitle: "Systems for accounting workflows.",
      systems: "Document extraction from tax forms and financial statements|Client inquiry automation with compliance-aware responses|Knowledge retrieval systems for tax code and regulatory guidance|Engagement letter and proposal automation|Work paper review and quality assurance systems|Internal knowledge assistants for staff training and reference",
      solutionsLabel: "Relevant solutions",
      solutionsTitle: "Explore by system category.",
      solutions: "Document Intelligence|Knowledge Systems and Enterprise Retrieval|AI Agents and Workflow Automation|Customer Support Systems",
      solutionHrefs: "/solutions/document-intelligence|/solutions/knowledge-systems-enterprise-retrieval|/solutions/ai-agents-workflow-automation|/solutions/customer-support-service-systems",
      engagementLabel: "Typical engagements",
      engagementTitle: "How we deliver for accounting firms.",
      engagementDesc: "Most accounting firm engagements are delivered through Agentic AI for workflow automation, Generative AI for document intelligence, or Data Platforms when the firm needs to restructure its data foundation first.",
      engagements: "Agentic AI|Data Platforms",
      engagementHrefs: "/services/agentic-ai-workflows|/services/data-platforms",
      ctaHeadline: "Describe the workflow.",
      ctaDesc: "We will scope the system around your firm's specific requirements, compliance constraints, and capacity challenges.",
    },
    manufacturing: {
      label: "Industry",
      heroTitle: "AI for Manufacturers and Distributors",
      hubDesc: "Demand forecasting, supply chain automation, and operational intelligence built for manufacturing and distribution workflows.",
      heroSubtitle: "Production-grade AI systems for demand forecasting, inventory optimization, supply chain communication, and operational decision-making. Built for planner review, data quality, and the complexity of manufacturing and distribution.",
      painsLabel: "The challenge",
      painsTitle: "Where standard ERP and planning tools stop short.",
      painsP1: "Manufacturers and distributors operate with complex supply chains, volatile demand patterns, and thin margins. ERP systems manage transactions but cannot predict demand shifts, optimize inventory allocation, or automate the communication workflows that connect suppliers, warehouses, and customers.",
      painsP2: "AI systems for manufacturing must integrate with existing ERP and warehouse systems, handle messy operational data, and deliver recommendations that floor managers and planners can act on.",
      painsListLabel: "Common operational pain points",
      pains: "Demand forecasting that relies on spreadsheets and intuition|Inventory allocation decisions that ignore real-time signals|Manual supplier communication and order management|Quality control that catches problems after production|Disconnected data across ERP, WMS, and planning tools|No decision support for pricing, assortment, or procurement timing",
      systemsLabel: "What we build",
      systemsTitle: "Systems for manufacturing and distribution.",
      systems: "Demand forecasting and inventory optimization models|Supplier communication and order automation|Quality prediction and defect classification|Production scheduling optimization support|Procurement timing and pricing intelligence|Operational dashboards with decision-grade data",
      solutionsLabel: "Relevant solutions",
      solutionsTitle: "Explore by system category.",
      solutions: "Predictive Models and Decision Science|AI Agents and Workflow Automation|Data Platforms for AI",
      solutionHrefs: "/solutions/predictive-models-decision-science|/solutions/ai-agents-workflow-automation|/solutions/data-platforms-for-ai",
      engagementLabel: "Typical engagements",
      engagementTitle: "How we deliver for manufacturers.",
      engagementDesc: "Manufacturing engagements typically involve Predictive AI for forecasting and optimization, Agentic AI for workflow automation, and Data Platforms when the data foundation needs work first.",
      engagements: "Predictive AI|Agentic AI|Data Platforms",
      engagementHrefs: "/services/decision-intelligence|/services/agentic-ai-workflows|/services/data-platforms",
      ctaHeadline: "Describe the operation.",
      ctaDesc: "We will scope the system around your supply chain, production workflow, or data challenge.",
    },
    services: {
      label: "Industry",
      heroTitle: "AI for Service Businesses and Trades",
      hubDesc: "Workflow automation, scheduling intelligence, and client communication systems built for high-volume service operations.",
      heroSubtitle: "Production-grade AI systems for client intake, scheduling optimization, service communication, and operational automation. Built with human review where judgment, dispatch risk, or customer impact matters.",
      painsLabel: "The challenge",
      painsTitle: "Where service management software stops short.",
      painsP1: "Service businesses and trades handle high volumes of client requests, scheduling complexity, and communication overhead. CRM and field service tools manage contacts and appointments but cannot automate the intake, triage, and communication workflows that consume dispatcher and coordinator time.",
      painsP2: "AI systems for service businesses must be fast, reliable, and simple enough for field teams to use without training.",
      painsListLabel: "Common operational pain points",
      pains: "Client intake and triage that depends on phone calls and emails|Scheduling that cannot optimize for route, skill, and priority|Repetitive client communication consuming office staff|No systematic follow-up or service quality monitoring|Knowledge trapped in senior technicians and dispatchers|Quoting and estimation that relies on individual experience",
      systemsLabel: "What we build",
      systemsTitle: "Systems for service operations.",
      systems: "Client intake automation with intelligent triage|Scheduling optimization with route and skill matching|Automated service communication and follow-up|Knowledge retrieval for field technicians|Quoting and estimation support systems|Service quality monitoring and feedback loops",
      solutionsLabel: "Relevant solutions",
      solutionsTitle: "Explore by system category.",
      solutions: "AI Agents and Workflow Automation|Customer Support Systems|Knowledge Systems and Enterprise Retrieval",
      solutionHrefs: "/solutions/ai-agents-workflow-automation|/solutions/customer-support-service-systems|/solutions/knowledge-systems-enterprise-retrieval",
      engagementLabel: "Typical engagements",
      engagementTitle: "How we deliver for service businesses.",
      engagementDesc: "Service business engagements are typically delivered through Agentic AI for workflow automation and Generative AI for customer communication knowledge.",
      engagements: "Agentic AI",
      engagementHrefs: "/services/agentic-ai-workflows",
      ctaHeadline: "Describe the service workflow.",
      ctaDesc: "We will scope the system around your intake, scheduling, or communication challenge.",
    },
    ecommerce: {
      label: "Industry",
      heroTitle: "AI for Ecommerce Operations",
      hubDesc: "Customer intelligence, catalog optimization, and profitability measurement built for DTC and retail ecommerce at scale.",
      heroSubtitle: "Production-grade AI systems for customer intelligence, catalog optimization, support automation, and profitability measurement. Built with review, monitoring, and margin discipline for ecommerce operations.",
      painsLabel: "The challenge",
      painsTitle: "Where ecommerce platforms stop short.",
      painsP1: "Ecommerce operations generate enormous data volumes across customer behavior, product catalogs, marketing channels, and fulfillment. Platform tools handle transactions and basic analytics but cannot build the customer intelligence, predictive models, and decision systems that drive profitable growth at scale.",
      painsP2: "AI systems for ecommerce must integrate with existing platforms, handle real-time data, and deliver recommendations that merchandising and marketing teams can act on immediately.",
      painsListLabel: "Common operational pain points",
      pains: "Customer acquisition costs growing with no visibility into quality by channel|Merchandising decisions based on revenue, not profit contribution|Support volume growing faster than team capacity|No predictive models for churn, LTV, or demand|Catalog optimization relying on manual curation|Attribution and measurement that contradicts across platforms",
      systemsLabel: "What we build",
      systemsTitle: "Systems for ecommerce operations.",
      systems: "Customer LTV and churn prediction models|Profitability measurement by channel, cohort, and product|Support automation with product knowledge integration|Recommendation and personalization engines|Demand forecasting and inventory optimization|Catalog enrichment and search optimization",
      solutionsLabel: "Relevant solutions",
      solutionsTitle: "Explore by system category.",
      solutions: "Predictive Models and Decision Science|Customer Support Systems|Data Platforms for AI",
      solutionHrefs: "/solutions/predictive-models-decision-science|/solutions/customer-support-service-systems|/solutions/data-platforms-for-ai",
      engagementLabel: "Typical engagements",
      engagementTitle: "How we deliver for ecommerce.",
      engagementDesc: "Ecommerce engagements typically span multiple pillars: Predictive AI for forecasting, recommendation, and profitability measurement; Agentic AI for support and catalog automation; and Data Platforms when the data foundation needs work.",
      engagements: "Predictive AI|Data Platforms|Agentic AI",
      engagementHrefs: "/services/decision-intelligence|/services/data-platforms|/services/agentic-ai-workflows",
      ctaHeadline: "Describe the ecommerce challenge.",
      ctaDesc: "We will scope the system around your growth, measurement, or operations problem.",
    },
  },
  seo: {
    home: {
      title: "Production AI Systems for Workflows | Vert AI",
      description: "Production AI systems, data platforms, workflow automations, and decision engines built around real workflows, controls, and measurable value.",
      keywords: "production AI systems, AI systems build, data platforms for AI, decision systems, decision science consulting, AI consulting Canada, AI workflow automation, AI agents for business workflows",
    },
    services: {
      title: "Production AI Services, Data Platforms & Decision Systems | Vert AI",
      description: "Compare production AI systems, workflow automation, data platforms, decision intelligence, and AI readiness mapping services.",
      keywords: "AI services, production AI systems, agentic AI workflow automation, data platforms for AI, decision intelligence, AI readiness audit",
    },
    agenticAI: {
      title: "Agentic AI Development & Workflow Automation | Vert AI",
      description: "Bounded AI agents with routing, controls, fallback paths, and human-in-the-loop oversight. Automate high-value workflows with production-grade agentic systems.",
      keywords: "agentic AI, AI agents, workflow automation, autonomous agents, AI orchestration, human-in-the-loop",
    },
    generativeAI: {
      title: "Generative AI & Retrieval Systems for Enterprise | Vert AI",
      description: "Retrieval-grounded generative AI with citations, evaluation, and accuracy thresholds. Turn institutional knowledge into answers your teams can trust.",
      keywords: "generative AI, RAG, retrieval augmented generation, enterprise AI, knowledge systems",
    },
    predictiveAI: {
      title: "Predictive AI & ML Models for Production | Vert AI",
      description: "Production ML for churn, LTV, demand, recommendation, propensity, and profitability. Models deployed into the workflows where decisions actually happen.",
      keywords: "predictive AI, machine learning, churn prediction, LTV modeling, demand forecasting, recommendation systems",
    },
    dataPlatforms: {
      title: "Data Platforms for AI — Modern Data Architecture | Vert AI",
      description: "Source-of-truth architecture, semantic layer, quality controls, and AI-readiness instrumentation. The foundation production AI depends on.",
      keywords: "data platforms, data architecture, semantic layer, AI-ready data, data engineering Canada",
    },
    solutions: {
      title: "AI Solutions — Production Systems by Domain | Vert AI",
      description: "AI agents, document intelligence, knowledge systems, customer support automation, predictive models, and data platforms — built and deployed by one team.",
      keywords: "AI solutions, AI use cases, document intelligence, knowledge systems, predictive models",
    },
    aiAgents: {
      title: "AI Agents & Workflow Automation Systems | Vert AI",
      description: "Reduce manual throughput costs with bounded AI agents. Routing logic, controls, fallback paths, and human review at the points that matter.",
      keywords: "AI agents, workflow automation, process automation, intelligent automation",
    },
    documentIntelligence: {
      title: "Document Intelligence & Extraction Systems | Vert AI",
      description: "Production-grade document AI for contracts, invoices, forms, and unstructured PDFs. Extraction, classification, and validation with audit trails.",
      keywords: "document intelligence, document AI, OCR, intelligent document processing, IDP",
    },
    knowledgeSystems: {
      title: "Enterprise Knowledge Systems & Retrieval | Vert AI",
      description: "Retrieval-augmented assistants that reason across policies, SOPs, product data, and commercial documentation with source attribution and accuracy controls.",
      keywords: "knowledge systems, enterprise search, RAG, retrieval augmented, internal knowledge AI",
    },
    customerSupport: {
      title: "AI Customer Support & Service Systems | Vert AI",
      description: "Lift resolution speed and contain cost-per-ticket. Intent classification, knowledge-grounded responses, agent assist, and confidence-based escalation.",
      keywords: "AI customer support, customer service automation, agent assist, ticket deflection",
    },
    predictiveModels: {
      title: "Predictive Models & Decision Science Systems | Vert AI",
      description: "Production models for churn, LTV, demand, recommendation, and profitability — wired into the operational workflows where commercial decisions happen.",
      keywords: "predictive models, decision science, ML models, churn modeling, demand forecasting",
    },
    dataPlatformsForAI: {
      title: "Data Platforms for AI — Production Foundations | Vert AI",
      description: "Source-of-truth architecture, semantic layer, quality controls, and AI-readiness instrumentation. The data foundation your AI and analytics depend on.",
      keywords: "data platform, AI-ready data, modern data stack, semantic layer, data foundation",
    },
    industries: {
      title: "Industries — AI Systems by Sector | Vert AI",
      description: "Production AI systems for retail, CPG, franchise, professional services, B2B services, accounting, and manufacturing operators.",
      keywords: "AI by industry, retail AI, CPG AI, franchise AI, professional services AI, B2B services AI, accounting AI, manufacturing AI",
    },
    accounting: {
      title: "AI for Accounting Firms — Document & Review Workflows | Vert AI",
      description: "AI systems for accounting firms: document intelligence, workpaper review, client intake, knowledge retrieval, and audit-ready controls.",
      keywords: "AI accounting, audit automation, accounting firm AI, CPA technology",
    },
    manufacturing: {
      title: "AI for Manufacturing & Distribution Operations | Vert AI",
      description: "Demand forecasting, production planning, predictive maintenance, and supply chain optimization. AI systems built for manufacturing and distribution operators.",
      keywords: "manufacturing AI, distribution AI, demand forecasting, predictive maintenance, supply chain AI",
    },
    serviceBusinesses: {
      title: "AI for Service Businesses & Trades | Vert AI",
      description: "AI workflow systems for service businesses: intake, scheduling, client communication, quoting support, and operational follow-up.",
      keywords: "AI professional services, consulting AI, services firm automation",
    },
    ecommerce: {
      title: "AI for Ecommerce Operations & Growth | Vert AI",
      description: "Recommendation, demand forecasting, profitability measurement, support automation, and catalog intelligence for ecommerce and DTC operators.",
      keywords: "ecommerce AI, DTC AI, recommendation systems, ecommerce automation",
    },
    work: {
      title: "Selected Work Patterns | Production AI Proof | Vert AI",
      description: "Explore honest work patterns for production AI systems, workflow automation, AI-ready data foundations, and monitored decision workflows.",
      keywords: "selected work patterns, production AI proof, AI delivery standards, workflow automation evidence",
    },
    aiReadinessScorecard: {
      title: "AI Readiness Scorecard for Production Workflows | Vert AI",
      description: "Score whether a workflow, decision, or data problem is ready for production AI, and see what to fix before building.",
      keywords: "AI readiness scorecard, production AI readiness, AI workflow assessment, AI opportunity scoring",
    },
    insights: {
      title: "Insights — AI Strategy & Implementation Analysis | Vert AI",
      description: "Implementation-grade analysis on AI systems, data infrastructure, and the economics of commercial decision-making. Written for senior operators.",
      keywords: "AI insights, AI strategy, AI implementation, data engineering analysis",
    },
    vetVendor: {
      title: "How to Vet an AI Vendor — Buyer's Guide | Vert AI",
      description: "Separate real AI capability from polished marketing. The questions, criteria, and signals that distinguish production-grade vendors from prototype shops.",
      keywords: "AI vendor evaluation, AI procurement, choose AI consultant, AI buyer guide",
    },
    productionGrade: {
      title: "What 'Production-Grade AI' Actually Means | Vert AI",
      description: "What separates a demo from a system that runs in production. Engineering, monitoring, fallback logic, and operational discipline that AI requires.",
      keywords: "production AI, production grade AI, MLOps, AI deployment",
    },
    workflowsDeserveAI: {
      title: "Which Workflows Deserve AI — Selection Framework | Vert AI",
      description: "Where AI creates measurable value and where it does not. A diagnostic framework for choosing workflows that justify the engineering investment.",
      keywords: "AI use case selection, AI ROI, workflow automation criteria, AI prioritization",
    },
    about: {
      title: "About Vert AI | Production AI, Data & Decision Systems",
      description: "Learn how Vert AI helps operators build production-grade AI systems, data platforms, workflow automations, and decision engines.",
      keywords: "about Vert AI, production AI systems, data platforms, decision systems, Guillaume Belisle",
    },
    contact: {
      title: "Contact Vert AI | Production AI Fit Call",
      description: "Talk to Vert AI about production AI systems, workflow automation, data platforms, and decision intelligence.",
      keywords: "contact Vert AI, production AI fit call, AI workflow automation, data platforms, decision intelligence",
    },
    notFound: {
      title: "Page Not Found | Vert AI",
      description: "The page you are looking for does not exist or has moved.",
    },
    unsubscribe: {
      title: "Unsubscribe | Vert AI",
      description: "Manage your email preferences.",
    },
  },
};
